import { useId, useState } from 'react';
import { ListChecks, Pause, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import { createPrepTemplate, deletePrepRule, prepRuleIdFor, setPrepRule, setPrepRuleStatus, starterPrepTemplate, type AppState, type OpResult } from '@autoday/core';
import { getStore, useStore } from '../../store/appStore.ts';
import { Button, Sheet, inputCls } from '../../ui/kit.tsx';
import { errorText, toast } from '../../ui/toast.tsx';
import { randomId } from '../../platform.ts';
import { useLang } from '../../i18n/i18n.ts';
import { TemplateEditor } from './TemplateEditor.tsx';
import { hasStarterTemplate, ruleStatusText, sortedTemplates, starterCalendarId, templateSummary } from './prepText.ts';

async function run(fn: (s: AppState) => OpResult, ok: string): Promise<boolean> {
  try {
    await getStore().run(fn);
    toast({ kind: 'ok', text: ok });
    return true;
  } catch (e) {
    toast({ kind: 'error', text: errorText(e) });
    return false;
  }
}

/**
 * Förberedelseplaner: återanvändbara mallar och automatiska förslag per importkälla. Nås från Viktigt och
 * från en deadlines "Förbered". Regler är av som standard och skapas bara uttryckligen här eller efter ett
 * godkännande; förslag räknas när appen är öppen och läggs aldrig in utan godkännande.
 */
export function PrepPlansSheet({ onClose }: { onClose: () => void }) {
  const { state } = useStore();
  const { t, lang } = useLang();
  const ids = useId();
  const [editor, setEditor] = useState<{ id: string | null } | null>(null);
  const templates = sortedTemplates(state);
  const sources = Object.values(state.sources).sort((a, b) => a.name.localeCompare(b.name, lang));
  const now = () => getStore().clock.now();

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()} wide title={t('Förberedelseplaner', 'Preparation plans')} description={t('Beskriv en gång hur du brukar förbereda dig – AutoDay föreslår tider inför dina deadlines.', 'Describe once how you usually prepare – AutoDay suggests times before your deadlines.')}>
      <div className="grid gap-6 text-sm">
        <section aria-labelledby={`${ids}-tpl`} className="grid gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 id={`${ids}-tpl`} className="flex items-center gap-2 text-base font-semibold">
              <ListChecks size={18} aria-hidden className="text-accent" /> {t('Mallar', 'Templates')}
            </h3>
            <Button icon={<Plus size={16} aria-hidden />} onClick={() => setEditor({ id: null })}>
              {t('Ny mall', 'New template')}
            </Button>
          </div>
          {templates.length ? (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {templates.map((tpl) => (
                <li key={tpl.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium [overflow-wrap:anywhere]">{tpl.name}</span>
                    <span className="block text-xs text-muted [overflow-wrap:anywhere]">{templateSummary(tpl)}</span>
                  </span>
                  <Button className="min-h-10 px-3" icon={<Pencil size={16} aria-hidden />} onClick={() => setEditor({ id: tpl.id })}>
                    {t('Ändra', 'Edit')}
                    <span className="sr-only"> {tpl.name}</span>
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="rounded-lg border border-dashed border-border-strong p-3 text-muted">{t('Inga mallar ännu.', 'No templates yet.')}</p>
          )}
          {!hasStarterTemplate(state) && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-accent-line bg-accent-soft/40 p-3">
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{starterPrepTemplate(lang).name}</span>
                <span className="block text-xs text-muted">{templateSummary(starterPrepTemplate(lang, starterCalendarId(state)))}</span>
              </span>
              <Button
                onClick={() => void run((s) => createPrepTemplate(s, randomId('tpl_'), starterPrepTemplate(lang, starterCalendarId(s)), now()), t('Mallen är sparad.', 'The template is saved.'))}
              >
                {t('Lägg till som mall', 'Add as a template')}
              </Button>
            </div>
          )}
          <p className="text-xs text-muted">{t('Ändrar du en mall påverkas inte planer som redan lagts in – de har en egen kopia av mallen.', 'Changing a template does not affect plans already added – they keep their own copy of the template.')}</p>
        </section>

        <section aria-labelledby={`${ids}-rules`} className="grid gap-2">
          <h3 id={`${ids}-rules`} className="text-base font-semibold">
            {t('Automatiska förslag', 'Automatic suggestions')}
          </h3>
          <p className="text-muted">
            {t(
              'Välj en importkälla och en mall så föreslår AutoDay planer inför källans kommande deadlines (21 dagar framåt). Förslagen räknas när appen är öppen. Inget läggs in, flyttas eller tas bort utan att du godkänner.',
              'Choose an import source and a template, and AutoDay suggests plans before the source’s upcoming deadlines (21 days ahead). Suggestions are worked out while the app is open. Nothing is added, moved or removed without your approval.',
            )}
          </p>
          {sources.length ? (
            <ul className="grid gap-2">
              {sources.map((src) => (
                <RuleRow key={src.id} sourceId={src.id} sourceName={src.name} />
              ))}
            </ul>
          ) : (
            <p className="rounded-lg border border-dashed border-border-strong p-3 text-muted">{t('Ingen kalender importerad ännu. Importera en .ics-fil under Import.', 'No calendar imported yet. Import an .ics file under Import.')}</p>
          )}
        </section>
      </div>
      {editor && <TemplateEditor templateId={editor.id} onClose={() => setEditor(null)} onSaved={() => undefined} />}
    </Sheet>
  );
}

function RuleRow({ sourceId, sourceName }: { sourceId: string; sourceName: string }) {
  const { state } = useStore();
  const { t } = useLang();
  const id = useId();
  const templates = sortedTemplates(state);
  const rule = state.prepRules?.[prepRuleIdFor(sourceId)];
  const [choice, setChoice] = useState<string>(rule?.templateId && state.prepTemplates?.[rule.templateId] ? rule.templateId : (templates[0]?.id ?? ''));
  const templateId = state.prepTemplates?.[choice] ? choice : (templates[0]?.id ?? '');
  const now = () => getStore().clock.now();
  const ruleTpl = rule ? state.prepTemplates?.[rule.templateId] : undefined;

  return (
    <li className="grid gap-2 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium [overflow-wrap:anywhere]">{sourceName}</span>
        <span className={`text-xs ${rule?.status === 'active' ? 'text-ok' : 'text-muted'}`}>{ruleStatusText(rule, ruleTpl?.name ?? null)}</span>
      </div>
      {templates.length ? (
        <div className="flex flex-wrap items-end gap-2">
          <label className="grid min-w-0 flex-1 gap-1 text-xs text-muted" htmlFor={`${id}-tpl`}>
            {t('Mall', 'Template')}
            <select
              id={`${id}-tpl`}
              className={`${inputCls} min-w-0`}
              value={templateId}
              onChange={(e) => {
                setChoice(e.target.value);
                if (rule) void run((s) => setPrepRule(s, randomId('rule_'), { sourceId, templateId: e.target.value, status: rule.status === 'active' ? 'active' : 'paused' }, now()), t('Regeln använder nu den valda mallen.', 'The rule now uses the chosen template.'));
              }}
            >
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </option>
              ))}
            </select>
          </label>
          {!rule && (
            <Button variant="primary" onClick={() => void run((s) => setPrepRule(s, randomId('rule_'), { sourceId, templateId, status: 'active' }, now()), t('Automatiska förslag är på för källan.', 'Automatic suggestions are on for the source.'))}>
              {t('Föreslå automatiskt', 'Suggest automatically')}
            </Button>
          )}
          {rule?.status === 'active' && (
            <Button icon={<Pause size={16} aria-hidden />} onClick={() => void run((s) => setPrepRuleStatus(s, randomId('rule_'), rule.id, 'paused', now()), t('Automatiska förslag är pausade. Redan bokade pass ligger kvar.', 'Automatic suggestions are paused. Sessions already booked stay.'))}>
              {t('Pausa', 'Pause')}
            </Button>
          )}
          {rule && rule.status !== 'active' && (
            <Button
              variant="primary"
              icon={<Play size={16} aria-hidden />}
              onClick={() =>
                void run((s) => {
                  // Mallen kan ha bytts eller tagits bort: återuppta med den valda mallen.
                  const r = s.prepRules[rule.id]!;
                  return r.templateId === templateId ? setPrepRuleStatus(s, randomId('rule_'), rule.id, 'active', now()) : setPrepRule(s, randomId('rule_'), { sourceId, templateId, status: 'active' }, now());
                }, t('Automatiska förslag är på igen.', 'Automatic suggestions are on again.'))
              }
            >
              {t('Återuppta', 'Resume')}
            </Button>
          )}
          {rule && (
            <Button variant="danger" icon={<Trash2 size={16} aria-hidden />} onClick={() => void run((s) => deletePrepRule(s, randomId('rule_'), rule.id, now()), t('Regeln är borttagen. Redan bokade pass ligger kvar.', 'The rule is removed. Sessions already booked stay.'))}>
              {t('Ta bort regeln', 'Remove the rule')}
              <span className="sr-only"> {sourceName}</span>
            </Button>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted">{t('Skapa en mall först.', 'Create a template first.')}</p>
      )}
    </li>
  );
}
